import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";

import { observer } from "mobx-react";

import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import LoaderSpinner from "@components-shared/LoaderSpinner";
import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { ChatEntryPoint } from "@data-types/chat";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import { workingHours } from "@features/chat/shared";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { redirect } from "@state";
import { getCurrentCulture, setTabTitle } from "@utils";

import SupportChatArea from "./SupportChatArea";

export default observer(function ChatSupportPage() {
	const { chatViewStore, diagnosticsProvider, userAuthStore } =
		useRootAppStore();

	const { t } = useTranslation();

	useScrollRestoration({ type: ConfigType.Basic });

	useEffect(() => {
		setTabTitle(t("CHAT.SUPPORT.TITLE"));
		chatViewStore.toggleChatWidget(false);
		chatViewStore.onInitialize(ChatEntryPoint.SUPPORT, null);

		return () => {
			chatViewStore.closeSupportChat();
		};
	}, []);

	if (!WEBPACK_IS_SUPPORT_CHAT_AVAILABLE || !userAuthStore.isLoggedIn) {
		return null;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatSupportPage.tsx"}
		>
			{!chatViewStore.chatBoxVisible && (
				<LoaderSpinner isLoading={chatViewStore.chatLoader.isLoading} />
			)}
			<DesktopLayout headerType={HeaderType.WITH_SIDEBAR}>
				<section className="l--content l--content--full">
					<div className="l--support l--content__main">
						<div className="l--support__container container">
							<div className="l--support__card card--primary">
								<div className="l--support__header card--primary__header">
									<div className="card--primary__title title--primary u-type--lrg">
										{t("CHAT.SUPPORT.TITLE")}
									</div>

									<InfoMessageComponent
										type="note"
										size="sml"
										addClassName="l--support__info"
										customIcon="u-icon--big u-icon--working-hours u-color--sent--note"
										message={t(
											"CHAT.SUPPORT.WORKING_HOURS",
											{
												"0": workingHours.from,
												"1": workingHours.to,
											}
										)}
									/>
								</div>
								<div className="l--support__body card--primary__body u-padd--reset">
									<SupportChatArea
										diagnostics={diagnosticsProvider}
										store={chatViewStore}
									/>
								</div>
							</div>
						</div>
					</div>
				</section>
				<aside className="l--aside--right">
					<div className="widget">
						<div className="widget__header">
							<div className="widget__header__title">
								{t("LIVE.INFO")}
							</div>
						</div>
						<div className="widget__body">
							<p>{t("CHAT.SUPPORT.INFO.WELCOME_SENTENCE")}</p>
							<ul className="list list--disc u-mar--bottom--xtny">
								<li>
									{t("CHAT.SUPPORT.INFO.ISSUE_INSTRUCTION")}
								</li>
								<li>
									{t(
										"CHAT.SUPPORT.INFO.UPLOAD_DOCUMENT_INSTRUCTION"
									)}
								</li>
							</ul>
							<p>
								{t(
									"CHAT.SUPPORT.INFO.THANK_YOU_SUPPORT_SENTENCE"
								)}
							</p>
						</div>
						<div className="widget__footer">
							<button
								onClick={() =>
									redirect(
										`/${getCurrentCulture()}/info/help`
									)
								}
								type="button"
								className="btn btn--base btn--block btn--primary--inverted btn--icon"
							>
								<span className="btn__label">
									{t<string>("FOOTER.HELP")}
								</span>
								<i className="u-icon u-icon--tny u-icon--arrow-right"></i>
							</button>
						</div>
					</div>
				</aside>
			</DesktopLayout>
		</ErrorBoundaryWithPropagation>
	);
});
